// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type ReactNode } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import Swal, { type SweetAlertOptions } from 'sweetalert2';
import { Modal } from './modal';
import styles from './modal.module.css';
import { AppIcon } from '@shared/ui-kit/components/app-icon';

export type ShowModalButton = 'close' | 'cancel' | 'deny' | 'confirm';

export interface ShowModalOptions
  extends Pick<SweetAlertOptions, 'preConfirm' | 'allowOutsideClick' | 'didOpen'> {
  readonly title?: ReactNode;

  readonly content?: ReactNode;

  readonly icon?: ReactNode;

  readonly variant?: 'overflow' | 'box';

  readonly buttons?: Partial<
    Record<ShowModalButton, { show?: boolean; text?: ReactNode | string }>
  >;

  readonly input?: Partial<{
    type: SweetAlertOptions['input'];
    placeholder: string;
  }>;

  readonly reverseButtons?: boolean;

  readonly width?: number | `${number}${'px' | '%' | undefined}`;
}

/**
 * Función auxiliar para procesar el texto de los botones. Si es un string (puede
 * contener HTML), se pasa directo. Si es un elemento React, se convierte a markup
 * estático.
 */
const getButtonText = (
  text: ReactNode | string | undefined,
  defaultText: string,
): string => {
  if (!text) return defaultText;
  if (typeof text === 'string') return text;
  return renderToStaticMarkup(text as ReactNode);
};

export const showModal = <T,>({
  title,
  content,
  icon,
  variant = 'box',
  buttons = {},
  input = {},
  reverseButtons = true,
  width,
  ...props
}: ShowModalOptions) =>
  Swal.fire<T>({
    html: renderToStaticMarkup(
      <Modal title={title} variant={variant} icon={icon}>
        {content}
      </Modal>,
    ),
    showCancelButton: buttons?.cancel?.show,
    showDenyButton: buttons?.deny?.show,
    showCloseButton: buttons?.close?.show,
    showConfirmButton: buttons?.confirm?.show,
    cancelButtonText: getButtonText(buttons?.cancel?.text, 'Cancel'),
    denyButtonText: getButtonText(buttons?.deny?.text, 'No'),
    confirmButtonText: getButtonText(buttons?.confirm?.text, 'OK'),
    input: input?.type,
    inputPlaceholder: input?.placeholder,
    reverseButtons,
    width,
    showLoaderOnConfirm: !!props.preConfirm,
    showLoaderOnDeny: !!props.preConfirm,
    closeButtonHtml: renderToStaticMarkup(
      <AppIcon icon="CloseOutline" size={20} />,
    ),
    customClass: {
      popup: [
        styles.popup,
        variant === 'overflow' ? styles['popup-overflow'] : '',
      ].filter(Boolean),
      title: [styles.title],
      htmlContainer: styles['html-container'],
      actions: styles.actions,
      closeButton: styles['close-button'],
      confirmButton: [styles.button, styles['button-confirm']],
      denyButton: [styles.button, styles['button-cancel']],
      cancelButton: [styles.button, styles['button-cancel']],
      input: [styles.input],
    },
    ...props,
  });
